<!-- eslint-disable vue/multi-word-component-names -- Existing component, kept for backward compatibility -->
<template>
    <div class="text-center leading-tight">
        <template v-if="hrisData || hasPrefillData">
            <h2 ref="headingRef" tabindex="-1" class="max-w-xl title outline-none">
                <span class="text-ink">{{ t("onboarding.personalization.knownTitle") }}</span>
            </h2>
            <p class="text-ink-muted text-center title-subsection mt-3 max-w-xl">{{ t("onboarding.personalization.knownSubtitle") }}</p>
        </template>
        <template v-else>
            <h2 ref="headingRef" tabindex="-1" class="max-w-xl title outline-none">
                <span class="text-ink">{{ t("onboarding.personalization.meetTitle") }}</span>
                <br />{{ t("onboarding.personalization.meetTitleRest") }}
            </h2>
            <p class="text-ink-muted text-center title-subsection mt-3 max-w-xl">{{ t("onboarding.personalization.meetSubtitle") }}</p>
        </template>
    </div>

    <form class="flex flex-col gap-10 md:gap-10 items-center w-full max-w-xl mt-6" @submit.prevent="onSubmit">
        <HrisProfileCard v-if="hrisData" v-model:name="userName" v-model:role="userRole" :hris-data="hrisData" />

        <!-- Standard inputs when no HRIS data -->
        <div v-else class="flex flex-col gap-2 w-full justify-center">
            <div class="form-control">
                <input
                    id="name"
                    v-model="userName"
                    :aria-label="t('onboarding.personalization.nameLabel')"
                    autocomplete="name"
                    required
                    type="text"
                    class="form-control-input p-6"
                    :placeholder="t('onboarding.personalization.nameLabel')"
                    :aria-invalid="formFieldHasError('name') || undefined"
                    :aria-describedby="formFieldHasError('name') ? 'name-error' : undefined" />
                <div v-if="formFieldHasError('name')" id="name-error" role="alert" class="body-small text-notification-error-text">{{ errors.name }}</div>
            </div>
            <div class="form-control">
                <input
                    id="role"
                    v-model="userRole"
                    :aria-label="t('onboarding.personalization.roleLabel')"
                    autocomplete="organization-title"
                    required
                    type="text"
                    class="form-control-input"
                    :placeholder="t('onboarding.personalization.roleLabel')"
                    :aria-invalid="formFieldHasError('job_title') || undefined"
                    :aria-describedby="formFieldHasError('job_title') ? 'role-error' : undefined" />
                <div v-if="formFieldHasError('job_title')" id="role-error" role="alert" class="body-small text-notification-error-text">{{ errors.job_title }}</div>
            </div>
        </div>

        <BaseButton theme="primary" class="w-75 leading-6 body-small" type="submit" data-testid="submit" :disabled="!userName.trim() || !userRole.trim()">{{
            t("onboarding.personalization.nextButton")
        }}</BaseButton>
    </form>
</template>

<script setup>
import BaseButton from "~vue/components/BaseButton.vue";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import HrisProfileCard from "./HrisProfileCard.vue";

const { t } = useI18n();

const props = defineProps({
    errors: Object,
    hrisData: Object,
    // BRA-1022: fallback name/job_title from the user record and SSO/SCIM ingest,
    // used for whichever field hrisData doesn't already supply.
    userPrefill: Object,
});

const emit = defineEmits(["submit"]);
const headingRef = ref(null);
const userName = ref("");
const userRole = ref("");

const hasPrefillData = computed(() => Boolean(props.userPrefill?.name || props.userPrefill?.job_title));

onMounted(() => {
    const first = props.hrisData?.first_name || "";
    const last = props.hrisData?.last_name || "";
    const hrisFullName = [first, last].filter(Boolean).join(" ");

    userName.value = hrisFullName || props.userPrefill?.name || "";
    userRole.value = props.hrisData?.job_title || props.userPrefill?.job_title || "";

    headingRef.value?.focus();
});

function formFieldHasError(field) {
    return props.errors && Object.hasOwn(props.errors, field);
}

function onSubmit() {
    emit("submit", {
        name: userName.value,
        job_title: userRole.value,
    });
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    .form-control {
        @apply flex flex-col gap-1;
    }

    .form-control-input {
        @apply w-full p-6 leading-none outline-control-accent rounded-xl border-2 body-bold text-control-text border-control-border bg-control-bg;
    }
}
</style>
